import { noop } from 'lodash';
import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';

import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';

export enum DialogID {
  INSURANCE_WARNING = 'INSURANCE_WARNING',
  ADDRESS_BLOCKED = 'ADDRESS_BLOCKED',
  ENERGY_BLOCKED = 'ENERGY_BLOCKED',
  RESTORE_SESSION = 'RESTORE_SESSION',
  RESTORE_VERIFY = 'RESTORE_VERIFY',
  OUTDATED_BUNDLE = 'OUTDATED_BUNDLE',
  UPDATED_ENERGY_TARIFF = 'UPDATED_ENERGY_TARIFF',
  INBOUND_CALL = 'INBOUND_CALL',
  UPDATING_BUNDLE = 'UPDATING_BUNDLE',
  REMOVE_FROM_BUNDLE = 'REMOVE_FROM_BUNDLE',
  START_AGAIN = 'START_AGAIN',
  NOT_FOUND_OTS = 'NOT_FOUND_OTS',
  FINDING_OTS = 'FINDING_OTS',
  FOUND_OTS = 'FOUND_OTS',
  MARKETING_CONSENT = 'MARKETING_CONSENT',
  SAVE_CHANGES_ENERGY = 'SAVE_CHANGES_ENERGY',
  SAVE_CHANGES_MOBILE = 'SAVE_CHANGES_MOBILE',
  PAPER_BILLING = 'PAPER_BILLING',
  WELCOME_BONUS = 'WELCOME_BONUS',
  ADDRESS_ALREADY_SUPPLIED = 'ADDRESS_ALREADY_SUPPLIED',
  DEBUG = 'DEBUG',

  // Appointments
  APMT_REQUEST_CONTROL = 'APMT_REQUEST_CONTROL',
  APMT_END = 'APMT_END',
  SUPPORT_LOGIN = 'SUPPORT_LOGIN',
  APMT_ERROR = 'APMT_ERROR',
  APMT_INFO = 'APMT_INFO',
  APMT_CONTROL_REQUEST_LOADING = 'APMT_CONTROL_REQUEST_LOADING',
  APMT_GIVING_CONTROL = 'APMT_GIVING_CONTROL',

  // In-life
  IN_LIFE_START_AGAIN = 'IN_LIFE_START_AGAIN',
  IN_LIFE_SESSION_TIMED_OUT = 'IN_LIFE_SESSION_TIMED_OUT',
  IN_LIFE_ENERGY_TARIFFS_UNAVAILABLE = 'IN_LIFE_ENERGY_TARIFFS_UNAVAILABLE',
  IN_LIFE_EXIT = 'IN_LIFE_EXIT',
}
const doNotSync = [
  DialogID.APMT_END,
  DialogID.APMT_ERROR,
  DialogID.APMT_INFO,
  DialogID.APMT_CONTROL_REQUEST_LOADING,
  DialogID.APMT_GIVING_CONTROL,
];

interface DialogState {
  id: DialogID;
  open: boolean;
  setOpen: (open: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  sync: boolean;
  isLoading: boolean;
  variant: string;
  setVariant: (variant: string) => void;
}

export type DialogStore = {
  [id: string]: DialogState;
};

const initialStore = Object.values(DialogID).reduce((acc, id) => {
  acc[id] = {
    id,
    open: false,
    sync: !doNotSync.includes(id),
    isLoading: false,
    setOpen: noop,
    setIsLoading: noop,
    variant: '',
    setVariant: noop,
  };
  return acc;
}, {} as DialogStore);

export const useDialogStore = create<DialogStore>((set) => {
  const store = { ...initialStore };

  for (const id of Object.values(DialogID)) {
    store[id] = {
      ...store[id],
      setOpen: (open) =>
        set((state) => {
          if (state[id].open === open) return state;
          return {
            ...state,
            [id]: {
              ...state[id],
              open,
            },
          };
        }),
      setIsLoading: (isLoading) =>
        set((state) => {
          if (state[id].isLoading === isLoading) return state;
          return {
            ...state,
            [id]: {
              ...state[id],
              isLoading,
            },
          };
        }),
      setVariant: (variant) =>
        set((state) => {
          if (state[id].variant === variant) return state;
          return {
            ...state,
            [id]: {
              ...state[id],
              variant,
            },
          };
        }),
    };
  }

  return store;
});

export const useDialog = (id: DialogID) => {
  const { hasControl, isSyncedAppointment } = useAppointmentContext();

  return useDialogStore(
    useShallow((state) => {
      const dialog = state[id];
      const shouldSync = dialog.sync && isSyncedAppointment;
      return hasControl || !shouldSync
        ? dialog
        : { ...dialog, setOpen: noop, setVariant: noop };
    })
  );
};

export const useDialogUnsynced = (id: DialogID) => {
  return useDialogStore(useShallow((state) => state[id]));
};

export const subscribeToDialogs = (
  callback: (id: DialogID, state: DialogState) => void
) => {
  let prevState = useDialogStore.getState();

  return useDialogStore.subscribe((newState) => {
    for (const dialogId of Object.values(DialogID)) {
      const prev = prevState[dialogId];
      const state = newState[dialogId];

      if (state.sync && JSON.stringify(prev) !== JSON.stringify(state)) {
        callback(dialogId, state);
      }
    }
    prevState = newState;
  });
};
